// TEMPLATES-T2..T4 (docs/design/2026-10-28-agent-templates.md §1–§6): the pure parts the
// Workforce › Templates screen, the Add-DE wizard's role path and the DE profile's access share —
// the SDK types, the queries and their keys, access levels (R read · A with approval · D direct ·
// M manage · none), money in minor units and the role KPIs as wizard rows.
// Zod-free: types only from the SDK (no `@companyos/schema/contracts` at runtime, CSP).
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type RoleTemplateList = OperationOutput<"roleTemplates.list">;
export type RoleCard = RoleTemplateList["data"][number];
export type VerticalPack = RoleTemplateList["vertical_packs"][number];
export type RolePlan = OperationOutput<"roleTemplates.plan">;
export type PlanAccess = RolePlan["access"][number];
export type PlanKpi = RolePlan["kpis"][number];
export type PlanConnection = RolePlan["required_connections"][number];
export type TemplateOverlay = OperationOutput<"templateOverlays.list">["data"][number];
export type DeAccess = OperationOutput<"digitalEmployees.access">;
export type DeAccessRow = DeAccess["data"][number];
export type DeProbation = NonNullable<DeAccess["probation"]>;
export type AccessFamily = PlanAccess["family"];
export type AccessLevel = DeAccessRow["level"];
export type Level4 = Exclude<AccessLevel, "none">;
export type ModelTier = RolePlan["model_tier"];
export type OverlayCreate = OperationInput<"templateOverlays.create">["body"];

export const ACCESS_FAMILIES: readonly AccessFamily[] = [
  "mail",
  "calendar",
  "message",
  "crm",
  "ledger",
  "social",
  "intake",
  "docs",
  "board",
  "chat",
  "kpis",
  "web",
  "recall",
  "render",
  "image",
];

export const FAMILY_LABELS: Record<AccessFamily, string> = {
  mail: "Mail",
  calendar: "Calendar",
  message: "Messages",
  crm: "CRM",
  ledger: "Ledger",
  social: "Social",
  intake: "Intake",
  docs: "Docs",
  board: "Boards",
  chat: "Chat",
  kpis: "KPIs",
  web: "Web",
  recall: "Memory",
  render: "Render",
  image: "Images",
};

export const LEVELS: readonly AccessLevel[] = ["none", "R", "A", "D", "M"];
export const LEVEL_LABELS: Record<AccessLevel, string> = {
  none: "None",
  R: "Read",
  A: "With approval",
  D: "Direct",
  M: "Manage",
};
export const SOURCE_LABELS: Record<DeAccessRow["source"], string> = {
  template: "Role",
  overlay: "Overlay",
  manual: "Set here",
};
export const TIER_LABELS: Record<ModelTier, string> = {
  light: "Light",
  standard: "Standard",
  heavy: "Heavy",
};
export const LAYER_LABELS: Record<string, string> = {
  role: "Role",
  vertical: "Vertical pack",
  overlay: "Overlay",
};

/** none < R < A < D < M; null (nothing granted) sits with none. */
export function levelRank(l: AccessLevel | null | undefined): number {
  return l ? LEVELS.indexOf(l) : 0;
}

/** The levels a select offers under a ceiling (null: no ceiling). */
export function levelsUpTo(cap: AccessLevel | null | undefined): AccessLevel[] {
  return LEVELS.filter((l) => cap == null || levelRank(l) <= levelRank(cap));
}

/** A level above the role's default needs an owner's or the supervisor's confirmation. */
export function widens(level: AccessLevel | null | undefined, role: AccessLevel | null): boolean {
  return levelRank(level) > levelRank(role);
}

export const levelText = (l: AccessLevel | null | undefined) => (l ? LEVEL_LABELS[l] : "None");

// Money: the API's currencies all have two minor-unit decimals (schema enums `currency`).
export const majorOf = (minor: number) => (minor / 100).toString();
export function minorOf(major: string): number | null {
  const t = major.trim();
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number.parseFloat(t) * 100);
}
export function formatMinor(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(minor / 100);
  } catch {
    return `${majorOf(minor)} ${currency}`;
  }
}

/** "a, b , c" → ["a", "b", "c"] (blank items dropped, duplicates once). */
export const parseList = (text: string) => [
  ...new Set(
    text
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean),
  ),
];

/** "replies_sent" → "Replies sent" (a KPI key with no company definition yet). */
export const humanKey = (key: string) => {
  const t = key.split(".").pop()?.replace(/_/g, " ") ?? key;
  return t.charAt(0).toUpperCase() + t.slice(1);
};

// Queries ----------------------------------------------------------------------------------------

export const templateKeys = {
  all: (co: string) => ["company", co, "role-templates"] as const,
  list: (co: string) => ["company", co, "role-templates", "list"] as const,
  plan: (co: string, templateId: string, de: string | null, vertical: string | null) =>
    ["company", co, "role-templates", "plan", templateId, de ?? "", vertical ?? ""] as const,
  overlays: (co: string) => ["company", co, "role-templates", "overlays"] as const,
  /** Under the DE's key, so `keys.de(co, id)` invalidates it too. */
  deAccess: (co: string, de: string) => ["company", co, "de", de, "access"] as const,
};

export const roleTemplatesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: templateKeys.list(co),
    queryFn: () => api.client.roleTemplates.list({ params: { co } }),
    staleTime: 60_000,
  });

export const rolePlanQuery = (
  api: Api,
  co: string,
  templateId: string,
  opts: { de?: string | null; vertical?: string | null } = {},
) =>
  queryOptions({
    queryKey: templateKeys.plan(co, templateId, opts.de ?? null, opts.vertical ?? null),
    queryFn: () =>
      api.client.roleTemplates.plan({
        params: { co, template_id: templateId },
        query: {
          ...(opts.de ? { de_membership_id: opts.de } : {}),
          ...(opts.vertical ? { vertical_overlay_id: opts.vertical } : {}),
        },
      }),
  });

export const overlaysQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: templateKeys.overlays(co),
    queryFn: () => api.client.templateOverlays.list({ params: { co } }),
  });

export const deAccessQuery = (api: Api, co: string, de: string) =>
  queryOptions({
    queryKey: templateKeys.deAccess(co, de),
    queryFn: () => api.client.digitalEmployees.access({ params: { co, id: de } }),
    retry: false,
  });

/** The applied vertical pack overlays of one role (the wizard's pack select). */
export const packOverlaysOf = (overlays: TemplateOverlay[], templateKey: string) =>
  overlays.filter(
    (o) => o.scope === "vertical" && o.template_key === templateKey && o.archived_at === null,
  );

// Wizard rows ------------------------------------------------------------------------------------

/** The role's required connections the DE must start without ("connect later" forced on). */
export const forcedLater = (plan: RolePlan) =>
  plan.required_connections.filter((c) => c.status === "not_available").map((c) => c.family);

/** Required connections not connected and not marked "connect later" (blocks the step). */
export const unresolvedConnections = (plan: RolePlan, later: readonly string[]) =>
  plan.required_connections.filter((c) => c.status !== "connected" && !later.includes(c.family));

/** Families the form sets above the role default without the confirmation ticked. */
export function unconfirmedWidening(
  plan: RolePlan,
  access: Partial<Record<AccessFamily, AccessLevel>>,
  confirmed: readonly string[],
): AccessFamily[] {
  return plan.access
    .filter((a) => {
      const l = access[a.family] ?? a.level;
      return widens(l, a.role) && !confirmed.includes(a.family);
    })
    .map((a) => a.family);
}

/** Only the families whose level the form changed (absent: the merged default). */
export function accessChanges(
  plan: RolePlan,
  access: Partial<Record<AccessFamily, AccessLevel>>,
): Partial<Record<AccessFamily, AccessLevel>> {
  const out: Partial<Record<AccessFamily, AccessLevel>> = {};
  for (const a of plan.access) {
    const l = access[a.family];
    if (l !== undefined && l !== (a.level ?? "none")) out[a.family] = l;
  }
  return out;
}
